import { useCallback, useEffect, useRef, useState } from "react";

/** Fetch a query key and ignore responses from superseded queries or unmounts. */
export function useApiQuery(queryFn, deps = [], { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(!skip);
  const hasLoadedOnce = useRef(false);
  const mounted = useRef(true);
  const requestId = useRef(0);
  const queryRef = useRef(queryFn);
  queryRef.current = queryFn;

  const run = useCallback(async () => {
    if (skip) return;
    const id = ++requestId.current;
    setIsFetching(true);
    setError(null);
    try {
      const result = await queryRef.current();
      if (!mounted.current || id !== requestId.current) return;
      setData(result);
      hasLoadedOnce.current = true;
      return result;
    } catch (err) {
      if (mounted.current && id === requestId.current) setError(err);
    } finally {
      if (mounted.current && id === requestId.current) setIsFetching(false);
    }
    // The caller supplies the query key rather than an unstable query function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip]);

  useEffect(() => {
    mounted.current = true;
    hasLoadedOnce.current = false;
    setData(null);
    setError(null);
    setIsFetching(!skip);
    run();
    return () => {
      mounted.current = false;
      requestId.current += 1;
    };
  }, [run, skip]);

  return { data, error, isFetching, isLoading: isFetching && !hasLoadedOnce.current, refetch: run };
}
